import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { getActiveCollectionId } from './collectionStore';
import { useWarehouseStore } from './warehouseStore';
import {
  ActivityLogRow,
  actionLabelForKey,
  movementActionKey,
  requestActionKey,
} from '../lib/stockActivity';

interface StockActivityState {
  rows: ActivityLogRow[];
  isLoading: boolean;
  error: string | null;
  fetchActivity: () => Promise<void>;
}

type LogJoinRow = {
  id: string;
  stock_unit_id: string;
  event_type: string;
  description: string | null;
  reference: string | null;
  movement_type: string | null;
  acted_by?: string | null;
  created_at: string;
  stock_units?: {
    unit_id: number;
    serial_mac: string | null;
    item_id: string;
    warehouse_id: string;
  } | null;
};

type RequestEventJoinRow = {
  id: string;
  request_id: string;
  event_type: string;
  description: string | null;
  user_display_name: string;
  created_at: string;
  stock_requests?: {
    request_number: number;
    collection_id: string;
  } | { request_number: number; collection_id: string }[] | null;
};

function first<T>(v: T | T[] | null | undefined): T | null {
  if (v == null) return null;
  return Array.isArray(v) ? (v[0] ?? null) : v;
}

const FETCH_LIMIT = 1500;

export const useStockActivityStore = create<StockActivityState>((set) => ({
  rows: [],
  isLoading: false,
  error: null,

  fetchActivity: async () => {
    set({ isLoading: true, error: null });
    const warehouseIds = useWarehouseStore.getState().warehouses.map(w => w.id);
    const collectionId = getActiveCollectionId();

    try {
      const logsQuery = warehouseIds.length > 0
        ? supabase
            .from('stock_logs')
            .select('*, stock_units!inner(unit_id, serial_mac, item_id, warehouse_id)')
            .in('stock_units.warehouse_id', warehouseIds)
            .order('created_at', { ascending: false })
            .limit(FETCH_LIMIT)
        : Promise.resolve({ data: [] as LogJoinRow[], error: null });

      const eventsQuery = collectionId
        ? supabase
            .from('stock_request_events')
            .select('id, request_id, event_type, description, user_display_name, created_at, stock_requests!inner(request_number, collection_id)')
            .eq('stock_requests.collection_id', collectionId)
            .order('created_at', { ascending: false })
            .limit(FETCH_LIMIT)
        : Promise.resolve({ data: [] as RequestEventJoinRow[], error: null });

      const [logsRes, eventsRes] = await Promise.all([logsQuery, eventsQuery]);

      if (logsRes.error) throw logsRes.error;
      if (eventsRes.error) throw eventsRes.error;

      const movementRows: ActivityLogRow[] = ((logsRes.data ?? []) as LogJoinRow[]).map(row => {
        const unit = first(row.stock_units);
        const actionKey = movementActionKey(row.event_type, row.movement_type);
        return {
          id: `m:${row.id}`,
          source: 'movement',
          createdAt: new Date(row.created_at),
          actionKey,
          actionLabel: actionLabelForKey(actionKey),
          itemId: unit?.item_id ?? null,
          itemName: null,
          detail: row.description,
          user: row.acted_by ?? null,
          reference: row.reference,
          unitLabel: unit ? `#${unit.unit_id}` : null,
          serialMac: unit?.serial_mac ?? null,
        };
      });

      const requestRows: ActivityLogRow[] = ((eventsRes.data ?? []) as RequestEventJoinRow[]).map(row => {
        const req = first(row.stock_requests);
        const actionKey = requestActionKey(row.event_type);
        const ref = req ? `SR-${req.request_number}` : null;
        return {
          id: `r:${row.id}`,
          source: 'request',
          createdAt: new Date(row.created_at),
          actionKey,
          actionLabel: actionLabelForKey(actionKey),
          itemId: null,
          itemName: null,
          detail: row.description,
          user: row.user_display_name || null,
          reference: ref,
          unitLabel: null,
          serialMac: null,
        };
      });

      const rows = [...movementRows, ...requestRows].sort(
        (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
      );
      set({ rows, isLoading: false, error: null });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load activity log.',
      });
    }
  },
}));
